import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const products = [
  {
    id: 'app',
    name: 'iOS App',
    tagline: 'Scan labels in-store',
    description: 'Point your camera at any ingredient label for instant analysis.',
    image: '/images/scan.png',
    secondaryImage: '/images/analyze.png',
    imageLayout: 'side-by-side',
    borderColor: 'border-sage-200',
    bgColor: 'bg-sage-50',
  },
  {
    id: 'extension',
    name: 'Browser Extension',
    tagline: 'Shop cleaner online',
    description: 'Auto-scans products on Amazon, Walmart, Whole Foods & more.',
    image: '/images/extension-action.png',
    secondaryImage: '/images/extension.png',
    imageLayout: 'overlay',
    borderColor: 'border-teal-500/30',
    bgColor: 'bg-teal-500/5',
  },
  {
    id: 'meal-planner',
    name: 'AI Meal Planner',
    tagline: 'Coming soon',
    description: 'Personalized meal plans that avoid your flagged ingredients.',
    image: null,
    borderColor: 'border-warm-500/30',
    bgColor: 'bg-warm-500/5',
    comingSoon: true,
  },
];

function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-rotate every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % products.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative overflow-hidden bg-gradient-hero noise-overlay pt-12 pb-16 md:pt-16 md:pb-20">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-sage-200 rounded-full blur-3xl opacity-40" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-sage-100 rounded-full blur-3xl opacity-50" />

      <div className="section relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left animate-fade-in-up">
            {/* Problem-focused headline */}
            <p className="text-sage-600 font-medium mb-3">
              Allergens, celiac, seed oils, and 15+ dietary concerns
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-tight mb-4">
              Scan for allergens and ingredients — <span className="text-sage-500">in-store or online.</span>
            </h1>

            <p className="text-lg text-gray-500 mb-6 max-w-md mx-auto lg:mx-0">
              The ingredient scanner that works everywhere you buy food. Free allergen and medical condition detection, always.
            </p>

            {/* Medical & Allergen Lens Badges */}
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-4">
              {[
                { name: 'Celiac', icon: '🌾' },
                { name: 'Nut', icon: '🥜' },
                { name: 'Soy', icon: '🫘' },
                { name: 'Egg', icon: '🥚' },
                { name: 'Dairy', icon: '🥛' },
                { name: 'PKU', icon: '⚕️' },
              ].map((lens) => (
                <span key={lens.name} className="inline-flex items-center gap-1 bg-white/80 text-charcoal text-xs font-medium px-2.5 py-1 rounded-full border border-sage-200">
                  <span>{lens.icon}</span> {lens.name}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
              <a href="#products" className="btn-primary text-lg px-8 py-4">
                See Your Options
              </a>
              <Link to="/ingredients" className="btn-secondary text-lg px-8 py-4">
                What We Detect
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              <span className="trust-badge">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                No ads, ever
              </span>
              <span className="trust-badge">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Your data stays yours
              </span>
            </div>
          </div>

          {/* Product Carousel */}
          <div className="flex justify-center lg:justify-end animate-fade-in-up animate-delay-200">
            <div className="relative w-full max-w-sm lg:max-w-md">
              {/* Carousel Container */}
              <div className="relative">
                {products.map((product, index) => (
                  <div
                    key={product.id}
                    className={`transition-all duration-500 ease-in-out ${
                      index === activeIndex
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 absolute inset-0 translate-x-4'
                    }`}
                  >
                    {/* Product Card */}
                    <div className={`bg-surface rounded-3xl shadow-elevated p-4 sm:p-6 border-2 ${product.borderColor}`}>
                      {/* Coming Soon Badge */}
                      {product.comingSoon && (
                        <div className="absolute top-3 right-3 bg-coral-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          Coming Soon
                        </div>
                      )}

                      {/* Image Container */}
                      <div className={`${product.bgColor} rounded-2xl p-3 mb-4 flex items-center justify-center min-h-[260px] sm:min-h-[320px] relative`}>
                        {product.image && product.secondaryImage && product.imageLayout === 'side-by-side' ? (
                          /* Side by side layout for iOS app */
                          <div className="flex items-end justify-center gap-3 sm:gap-4">
                            <img
                              src={product.image}
                              alt={`${product.name} - Scan`}
                              className="max-h-[220px] sm:max-h-[280px] w-auto object-contain rounded-xl shadow-lg"
                            />
                            <img
                              src={product.secondaryImage}
                              alt={`${product.name} - Analyze`}
                              className="max-h-[220px] sm:max-h-[280px] w-auto object-contain rounded-xl shadow-lg"
                            />
                          </div>
                        ) : product.image && product.secondaryImage && product.imageLayout === 'overlay' ? (
                          /* Overlay layout for extension */
                          <div className="relative w-full h-full flex items-center justify-center">
                            <img
                              src={product.image}
                              alt={`${product.name} in action`}
                              className="max-h-[200px] sm:max-h-[240px] w-auto object-contain rounded-lg shadow-lg"
                            />
                            <img
                              src={product.secondaryImage}
                              alt={`${product.name} popup`}
                              className="absolute -bottom-2 -right-2 sm:bottom-0 sm:right-0 max-h-[120px] sm:max-h-[160px] w-auto object-contain rounded-lg shadow-xl border-2 border-white"
                            />
                          </div>
                        ) : product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="max-h-[240px] sm:max-h-[300px] w-auto object-contain rounded-xl shadow-lg"
                          />
                        ) : (
                          /* Custom Meal Planner Placeholder */
                          <div className="flex flex-col items-center justify-center w-full max-w-[280px]">
                            {/* Mockup meal plan card */}
                            <div className="bg-white rounded-2xl shadow-lg p-4 w-full">
                              {/* Header with AI sparkle */}
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-lg bg-warm-500/20 flex items-center justify-center">
                                    <svg className="w-5 h-5 text-warm-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  <span className="font-medium text-charcoal text-sm">Your Week</span>
                                </div>
                                {/* AI Sparkle */}
                                <div className="flex items-center gap-1 text-warm-500">
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2L13.09 8.26L19 7L14.74 11.27L21 12L14.74 12.73L19 17L13.09 15.74L12 22L10.91 15.74L5 17L9.26 12.73L3 12L9.26 11.27L5 7L10.91 8.26L12 2Z" />
                                  </svg>
                                  <span className="text-xs font-medium">AI</span>
                                </div>
                              </div>

                              {/* Meal slots */}
                              <div className="space-y-2">
                                {['Breakfast', 'Lunch', 'Dinner'].map((meal, i) => (
                                  <div key={meal} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                                    <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-orange-400' : 'bg-red-400'}`} />
                                    <span className="text-xs text-gray-600 flex-1">{meal}</span>
                                    <div className="w-16 h-2 bg-gray-200 rounded-full" />
                                  </div>
                                ))}
                              </div>

                              {/* Footer hint */}
                              <div className="mt-4 pt-3 border-t border-gray-100 text-center">
                                <span className="text-xs text-gray-400">Personalized for you</span>
                              </div>
                            </div>

                            <span className="text-sm font-medium text-warm-500 mt-4">In Development</span>
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="text-center">
                        <h3 className="font-display text-xl sm:text-2xl text-charcoal mb-1">{product.name}</h3>
                        <p className="text-sage-600 font-medium text-sm mb-2">{product.tagline}</p>
                        <p className="text-gray-500 text-sm">{product.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Carousel Indicators */}
              <div className="flex justify-center gap-2 mt-6">
                {products.map((product, index) => (
                  <button
                    key={product.id}
                    onClick={() => setActiveIndex(index)}
                    className={`transition-all duration-300 rounded-full ${
                      index === activeIndex
                        ? 'w-8 h-2 bg-sage-500'
                        : 'w-2 h-2 bg-sage-300 hover:bg-sage-400'
                    }`}
                    aria-label={`View ${product.name}`}
                  />
                ))}
              </div>

              {/* Product Switcher Labels */}
              <div className="flex justify-center gap-4 mt-4">
                {products.map((product, index) => (
                  <button
                    key={product.id}
                    onClick={() => setActiveIndex(index)}
                    className={`text-xs font-medium transition-colors ${
                      index === activeIndex
                        ? 'text-sage-600'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {product.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="mt-16 pt-12 border-t border-sage-200/50">
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto lg:mx-0">
            <div className="text-center lg:text-left">
              <div className="font-display text-3xl sm:text-4xl text-sage-500 mb-1">16+</div>
              <div className="text-sm text-gray-500">Detection lenses</div>
            </div>
            <div className="text-center lg:text-left">
              <div className="font-display text-3xl sm:text-4xl text-sage-500 mb-1">500+</div>
              <div className="text-sm text-gray-500">Ingredients flagged</div>
            </div>
            <div className="text-center lg:text-left">
              <div className="font-display text-3xl sm:text-4xl text-sage-500 mb-1">Free</div>
              <div className="text-sm text-gray-500">Allergen detection</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
